import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';

import moment from 'moment';
import momentPropTypes from 'react-moment-proptypes';

import dateMessages from '@/shared/messages/dateMessages';

const periods = [
  {
    period: 'today-1',
    dateFrom: () =>
      moment
        .utc()
        .subtract(1, 'day')
        .startOf('day'),
    dateUntil: () =>
      moment
        .utc()
        .subtract(1, 'day')
        .endOf('day'),
    label: dateMessages.Yesterday,
  },
  {
    period: 'today',
    dateFrom: () => moment.utc().startOf('day'),
    dateUntil: () => moment.utc().endOf('day'),
    label: dateMessages.Today,
  },
  {
    period: 'week-1',
    dateFrom: () =>
      moment
        .utc()
        .subtract(7, 'day')
        .startOf('week'),
    dateUntil: () =>
      moment
        .utc()
        .subtract(7, 'day')
        .endOf('week'),
    label: dateMessages.PreviousWeek,
  },
  {
    period: 'week',
    dateFrom: () => moment.utc().startOf('week'),
    dateUntil: () => moment.utc().endOf('week'),
    label: dateMessages.ThisWeek,
  },
  {
    period: 'month-1',
    dateFrom: () =>
      moment
        .utc()
        .subtract(1, 'month')
        .startOf('month'),
    dateUntil: () =>
      moment
        .utc()
        .subtract(1, 'month')
        .endOf('month'),
    label: dateMessages.PreviousMonth,
  },
  {
    period: 'month',
    dateFrom: () => moment.utc().startOf('month'),
    dateUntil: () => moment.utc().endOf('month'),
    label: dateMessages.ThisMonth,
  },
  {
    period: 'nextMonth',
    dateFrom: () =>
      moment
        .utc()
        .add(1, 'month')
        .startOf('month'),
    dateUntil: () =>
      moment
        .utc()
        .add(1, 'month')
        .endOf('month'),
    label: dateMessages.NextMonth,
  },
  {
    period: 'month+1',
    dateFrom: () =>
      moment
        .utc()
        .add(1, 'month')
        .startOf('month'),
    dateUntil: () =>
      moment
        .utc()
        .add(1, 'month')
        .endOf('month'),
    label: dateMessages.NextMonth,
  },
  {
    period: 'months-6',
    dateFrom: () =>
      moment
        .utc()
        .subtract(6, 'month')
        .startOf('day'),
    dateUntil: () => moment.utc().endOf('day'),
    label: dateMessages.LastSixMonths,
  },
  {
    period: 'quarter-1',
    dateFrom: () =>
      moment
        .utc()
        .add(-3, 'months')
        .startOf('quarter'),
    dateUntil: () =>
      moment
        .utc()
        .add(-3, 'months')
        .endOf('quarter'),
    label: dateMessages.PreviousQuarter,
  },
  {
    period: 'quarter',
    dateFrom: () => moment.utc().startOf('quarter'),
    dateUntil: () => moment.utc().endOf('quarter'),
    label: dateMessages.ThisQuarter,
  },
  {
    period: 'quarter+1',
    dateFrom: () =>
      moment
        .utc()
        .add(3, 'months')
        .startOf('quarter'),
    dateUntil: () =>
      moment
        .utc()
        .add(3, 'months')
        .endOf('quarter'),
    label: dateMessages.NextQuarter,
  },
  {
    period: 'nextQuarter',
    dateFrom: () =>
      moment
        .utc()
        .add(3, 'months')
        .startOf('quarter'),
    dateUntil: () =>
      moment
        .utc()
        .add(3, 'months')
        .endOf('quarter'),
    label: dateMessages.NextQuarter,
  },
  {
    period: 'year-2',
    dateFrom: () =>
      moment
        .utc()
        .startOf('year')
        .subtract(2, 'y'),
    dateUntil: () =>
      moment
        .utc()
        .endOf('year')
        .subtract(2, 'y'),
    label: moment.utc().year() - 2,
  },
  {
    period: 'year-1',
    dateFrom: () =>
      moment
        .utc()
        .startOf('year')
        .subtract(1, 'y'),
    dateUntil: () =>
      moment
        .utc()
        .endOf('year')
        .subtract(1, 'y'),
    label: moment.utc().year() - 1,
  },
  {
    period: 'year',
    dateFrom: () => moment.utc().startOf('year'),
    dateUntil: () => moment.utc().endOf('year'),
    label: moment.utc().year(),
  },
  {
    period: 'year+1',
    dateFrom: () =>
      moment
        .utc()
        .startOf('year')
        .add(1, 'y'),
    dateUntil: () =>
      moment
        .utc()
        .endOf('year')
        .add(1, 'y'),
    label: moment.utc().year() + 1,
  },

  {
    period: 'all',
    dateFrom: () => null,
    dateUntil: () => null,
    label: dateMessages.All,
  },
];

export const allPeriods = periods.map(x => x.period);
export const defaultPeriods = ['month', 'quarter', 'year', 'year-1', 'year-2', 'all'];
export const pastToNowPeriods = [
  'today-1',
  'today',
  'week-1',
  'week',
  'month-1',
  'month',
  'quarter-1',
  'quarter',
  'year-1',
  'year',
  'all',
];
export const pastToPrevMonth = [
  'month-1',
  'month',
  'quarter-1',
  'quarter',
  'year-2',
  'year-1',
  'year',
  'all',
];

export const getPeriodStartEndDates = periodName => {
  const p = periods.find(x => x.period === periodName);
  if (!p) return [null, null, null];
  return [p.dateFrom(), p.dateUntil(), p.period];
};
export const getPeriodDateFromUntil = periodName => {
  const [dateFrom, dateUntil, period] = getPeriodStartEndDates(periodName);
  return { dateFrom, dateUntil, period };
};

// Execute date functions and get real dates
const getPeriodDates = ({ period, dateFrom, dateUntil }) => ({
  period,
  dateFrom: dateFrom(),
  dateUntil: dateUntil(),
});

export const getPeriodNameFromDates = (
  dateFrom,
  dateUntil,
  visiblePeriods = null,
  hiddenPeriods = null,
) => {
  if (!dateFrom && !dateUntil) return 'all';
  if (!dateFrom || !dateUntil) return null;
  const momentFrom = moment.utc(dateFrom);
  const momentUntil = moment.utc(dateUntil);

  return periods.find(
    p =>
      (!visiblePeriods || visiblePeriods.length === 0 || visiblePeriods.indexOf(p.period) >= 0) &&
      (!hiddenPeriods || hiddenPeriods.length === 0 || hiddenPeriods.indexOf(p.period) < 0) &&
      p.period !== 'all' &&
      momentFrom.isSame(p.dateFrom(), 'day') &&
      momentUntil.isSame(p.dateUntil(), 'day'),
  )?.period;
};

const PeriodButtonGroup = ({
  dateFrom,
  dateUntil,
  period,
  onChange,
  hiddenPeriods,
  visiblePeriods,
  className,
}) => {
  const filteredPeriods = periods.filter(
    p =>
      (visiblePeriods.length === 0 || visiblePeriods.indexOf(p.period) >= 0) &&
      hiddenPeriods.indexOf(p.period) < 0,
  );
  return (
    <div
      role="group"
      aria-label="period"
      className={classnames(className, 'btn-group btn-group-justified')}
    >
      {filteredPeriods.map(p => {
        const localPeriod = getPeriodDates(p);
        return (
          <button
            onClick={() => onChange(localPeriod)}
            type="button"
            key={`period-${p.period}`}
            className={classnames('btn btn-default', {
              active:
                period === p.period ||
                (p.period === 'all' && dateFrom === null && dateUntil === null) ||
                (dateFrom != null &&
                  dateUntil !== null &&
                  moment(dateFrom).isSame(localPeriod.dateFrom, 'day') &&
                  moment(dateUntil).isSame(localPeriod.dateUntil, 'day')),
            })}
          >
            {p.label && p.label.id ? <FormattedMessage {...p.label} /> : p.label}
          </button>
        );
      })}
    </div>
  );
};

PeriodButtonGroup.propTypes = {
  dateFrom: momentPropTypes.momentObj,
  dateUntil: momentPropTypes.momentObj,
  period: PropTypes.oneOf(allPeriods),
  onChange: PropTypes.func.isRequired,
  visiblePeriods: PropTypes.arrayOf(PropTypes.oneOf(allPeriods)),
  hiddenPeriods: PropTypes.arrayOf(PropTypes.oneOf(allPeriods)),
  className: PropTypes.string,
};

PeriodButtonGroup.defaultProps = {
  dateFrom: null,
  dateUntil: null,
  period: 'all',
  visiblePeriods: defaultPeriods,
  hiddenPeriods: [],
  className: ' margin10R',
};
export default PeriodButtonGroup;
